import { init } from "./utils/initDroneLayer.js";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/* ── Inline SVG Icon Components ── */
const Icon = ({ children, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    {children}
  </svg>
);

const IconRocket = () => (
  <Icon>
    <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
    <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
    <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
    <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
  </Icon>
);

const IconShield = () => (
  <Icon>
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="m9 12 2 2 4-4" />
  </Icon>
);

const IconGrad = () => (
  <Icon>
    <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
    <path d="M6 12v5c0 1.1 2.7 3 6 3s6-1.9 6-3v-5" />
  </Icon>
);

const IconBook = () => (
  <Icon>
    <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20" />
    <path d="M8 7h6" />
    <path d="M8 11h4" />
  </Icon>
);

const IconGoggles = () => (
  <Icon>
    <circle cx="7" cy="12" r="3" />
    <circle cx="17" cy="12" r="3" />
    <path d="M10 12h4" />
    <path d="M4 12H2" />
    <path d="M22 12h-2" />
  </Icon>
);

const IconScale = () => (
  <Icon>
    <path d="m16 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1z" />
    <path d="m2 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1z" />
    <path d="M7 21h10" />
    <path d="M12 3v18" />
    <path d="M3 7h2c2 0 5-1 7-2 2 1 5 2 7 2h2" />
  </Icon>
);

const IconLock = () => (
  <Icon>
    <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </Icon>
);

const IconFeather = () => (
  <Icon>
    <path d="M20.24 12.24a6 6 0 0 0-8.49-8.49L5 10.5V19h8.5z" />
    <line x1="16" y1="8" x2="2" y2="22" />
    <line x1="17.5" y1="15" x2="9" y2="15" />
  </Icon>
);

const IconWrench = () => (
  <Icon>
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
  </Icon>
);

function App() {
  const [email, setEmail] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const { t, i18n } = useTranslation();

  const toggleLang = () => {
    i18n.changeLanguage(i18n.language === "nl" ? "en" : "nl");
  };

  useEffect(() => {
    init();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    alert(t("waitlist_thanks", { email }));
    setEmail("");
  };

  return (
    <div className="App">
      {/* ── Noise overlay ── */}
      <div className="noise-overlay" />

      {/* ── Header ── */}
      <header className="header">
        <div className="header-inner">
          <a href="#hero" className="logo">
            FPV<span>easy</span>
          </a>
          <button
            className="menu-toggle"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <span />
            <span />
            <span />
          </button>
          <nav className={`nav ${menuOpen ? "nav--open" : ""}`}>
            <a href="#why" onClick={() => setMenuOpen(false)}>
              {t("nav_why")}
            </a>
            <a href="#learning" onClick={() => setMenuOpen(false)}>
              {t("nav_learning")}
            </a>
            <a href="#safety" onClick={() => setMenuOpen(false)}>
              {t("nav_safety")}
            </a>
            <a href="#path" onClick={() => setMenuOpen(false)}>
              {t("nav_path")}
            </a>
            <button
              className="lang-toggle"
              onClick={toggleLang}
              aria-label="Switch language"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
                <path d="M2 12h20" />
              </svg>
              <span>{i18n.language === "nl" ? "EN" : "NL"}</span>
            </button>
            <a
              href="#waitlist"
              onClick={() => setMenuOpen(false)}
              className="nav-cta"
            >
              {t("nav_waitlist")}
            </a>
          </nav>
        </div>
      </header>

      {/* ── Hero ── */}
      <section className="hero" id="hero">
        <div className="hero-gradient" />
        <div className="container hero-content">
          <h1 className="hero-title">{t("hero_title")}</h1>
          <p className="hero-sub">{t("hero_sub")}</p>
          <p className="hero-desc">{t("hero_desc")}</p>
          <div className="hero-buttons">
            <a href="#waitlist" className="btn btn-primary">
              {t("hero_cta")}
            </a>
            <a href="#why" className="btn btn-outline">
              {t("hero_learn")}
            </a>
          </div>
        </div>
      </section>

      {/* ── Why FPVeasy ── */}
      <section className="section why" id="why">
        <div className="container">
          <p className="section-label">{t("why_label")}</p>
          <h2 className="section-title">{t("why_title")}</h2>
          <p className="section-subtitle">{t("why_subtitle")}</p>
          <div className="card-grid">
            <div className="card">
              <div className="card-icon">
                <IconRocket />
              </div>
              <h3>{t("why_card1_title")}</h3>
              <p>{t("why_card1_desc")}</p>
            </div>
            <div className="card">
              <div className="card-icon">
                <IconShield />
              </div>
              <h3>{t("why_card2_title")}</h3>
              <p>{t("why_card2_desc")}</p>
            </div>
            <div className="card">
              <div className="card-icon">
                <IconGrad />
              </div>
              <h3>{t("why_card3_title")}</h3>
              <p>{t("why_card3_desc")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Interactive Learning ── */}
      <section className="section learning" id="learning">
        <div className="container">
          <p className="section-label">{t("learning_label")}</p>
          <h2 className="section-title">{t("learning_title")}</h2>
          <p className="section-subtitle">{t("learning_subtitle")}</p>
          <a href="#waitlist" className="btn btn-primary">
            {t("learning_cta")}
          </a>
        </div>
      </section>

      {/* ── Safety ── */}
      <section className="section safety" id="safety">
        <div className="container">
          <p className="section-label">{t("safety_label")}</p>
          <h2 className="section-title">{t("safety_title")}</h2>
          <p className="section-subtitle">{t("safety_subtitle")}</p>

          <div className="safety-philosophy">
            <h3>{t("safety_philosophy_title")}</h3>
            <p>{t("safety_philosophy_desc")}</p>
          </div>

          <div className="card-grid card-grid--3x2">
            <div className="card">
              <div className="card-icon">
                <IconBook />
              </div>
              <h3>{t("safety_card1_title")}</h3>
              <p>{t("safety_card1_desc")}</p>
            </div>
            <div className="card">
              <div className="card-icon">
                <IconGoggles />
              </div>
              <h3>{t("safety_card2_title")}</h3>
              <p>{t("safety_card2_desc")}</p>
            </div>
            <div className="card">
              <div className="card-icon">
                <IconScale />
              </div>
              <h3>{t("safety_card3_title")}</h3>
              <p>{t("safety_card3_desc")}</p>
            </div>
            <div className="card">
              <div className="card-icon">
                <IconLock />
              </div>
              <h3>{t("safety_card4_title")}</h3>
              <p>{t("safety_card4_desc")}</p>
            </div>
            <div className="card">
              <div className="card-icon">
                <IconFeather />
              </div>
              <h3>{t("safety_card5_title")}</h3>
              <p>{t("safety_card5_desc")}</p>
            </div>
            <div className="card">
              <div className="card-icon">
                <IconWrench />
              </div>
              <h3>{t("safety_card6_title")}</h3>
              <p>{t("safety_card6_desc")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Learning Path ── */}
      <section className="section path" id="path">
        <div className="container">
          <h2 className="section-title">{t("path_title")}</h2>
          <p className="section-subtitle">{t("path_subtitle")}</p>

          <div className="steps">
            <div className="step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>{t("path_step1_title")}</h3>
                <p>{t("path_step1_desc")}</p>
              </div>
            </div>
            <div className="step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>{t("path_step2_title")}</h3>
                <p>{t("path_step2_desc")}</p>
              </div>
            </div>
            <div className="step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>{t("path_step3_title")}</h3>
                <p>{t("path_step3_desc")}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Waitlist / CTA ── */}
      <section className="section waitlist" id="waitlist">
        <div className="container">
          <h2 className="section-title">{t("waitlist_title")}</h2>
          <p className="section-subtitle">{t("waitlist_subtitle")}</p>
          <form
            action="https://formspree.io/f/xaqdrlgw"
            method="POST"
            className="waitlist-form"
            // onSubmit={handleSubmit}
          >
            <input
              type="email"
              name="email"
              placeholder={t("waitlist_placeholder")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />

            <button type="submit" className="btn btn-primary">
              {t("waitlist_btn")}
            </button>

            <textarea name="message" placeholder={t("message_placeholder")}></textarea>
          </form>

          {/* <form action="https://formspree.io/f/xaqdrlgw" method="POST">
            <label>
              Your email:
              <input type="email" name="email" />
            </label>
            <label>Your message:</label>

            <button type="submit">Send</button>
          </form> */}

          <div class="ml-embedded" data-form="1ZPKEa"></div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="footer">
        <div className="container">
          <p>{t("footer_built")}</p>
          <p>{t("footer_copy")}</p>
        </div>
      </footer>
    </div>
  );
}

export default App;
